// technology.jsx - PyK7VM Technology Deep-Dive & Architecture
// Compiled to dist/technology.js via build.mjs.

const { useState, useEffect, useRef, useCallback } = React;
const { Reveal, Aurora, useInView, useScrollRef } = window;
const { Nav, Footer, DiscordCTA } = window;

const _delay = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── PIPELINE STAGE DEFINITIONS ─────────────────────────────────────────────
const STAGE_SOURCE = `<span style="color:#94a3b8">def</span> <span style="color:#93c5fd">validate_license</span>(key: <span style="color:#60a5fa">str</span>, machine_id: <span style="color:#60a5fa">str</span>) -> <span style="color:#60a5fa">bool</span>:
    salt = <span style="color:#a3e635">"f9a2b1-internal-2026"</span>
    digest = sha256(
      f<span style="color:#a3e635">"{key}:{machine_id}:{salt}"</span>.encode()
    ).hexdigest()
    <span style="color:#60a5fa">return</span> check_server(digest)`;

const STAGE_AST = `<span style="color:#94a3b8">def</span> <span style="color:#93c5fd">_b7f2a9</span>(_x3c1, _q8m5):
    <span style="color:#6a7282"># Opaque predicate & randomized variable mapping</span>
    _r2k9 = _pk7vm_derive_token(<span style="color:#c084fc">0x7F</span>, seed=<span style="color:#c084fc">0x88B1</span>)
    _h4n7 = _d1p0(
      f<span style="color:#a3e635">"{_x3c1}:{_q8m5}:{_r2k9}"</span>.encode()
    ).hexdigest()
    <span style="color:#60a5fa">return</span> _z9w3(_h4n7)`;

const STAGE_BYTECODE = `<span style="color:#6a7282">; Custom VM Bytecode - opcodes randomly assigned per build</span>
<span style="color:#c084fc">0x0000</span>  <span style="color:#60a5fa">OP_4F</span>  idx=0x02   <span style="color:#6a7282">; LOAD_FAST      _x3c1 ('key')</span>
<span style="color:#c084fc">0x0004</span>  <span style="color:#60a5fa">OP_88</span>  idx=0x01   <span style="color:#6a7282">; LOAD_FAST      _q8m5 ('machine_id')</span>
<span style="color:#c084fc">0x0008</span>  <span style="color:#60a5fa">OP_1D</span>  idx=0x07   <span style="color:#6a7282">; LOAD_CONST_ENC &lt;encrypted_salt_seed&gt;</span>
<span style="color:#c084fc">0x000C</span>  <span style="color:#60a5fa">OP_A3</span>  argc=3     <span style="color:#6a7282">; BUILD_STRING</span>
<span style="color:#c084fc">0x0010</span>  <span style="color:#60a5fa">OP_C1</span>  argc=1     <span style="color:#6a7282">; CALL_METHOD    .encode</span>
<span style="color:#c084fc">0x0014</span>  <span style="color:#60a5fa">OP_B9</span>  idx=0x03   <span style="color:#6a7282">; CALL_FUNC      _d1p0 (sha256)</span>
<span style="color:#c084fc">0x0018</span>  <span style="color:#60a5fa">OP_F3</span>  argc=1     <span style="color:#6a7282">; RETURN_VAL</span>`;

const STAGE_CPP = `<span style="color:#6a7282">// Per-build polymorphic native dispatch engine (Build #9F2A)</span>
<span style="color:#60a5fa">__forceinline</span> <span style="color:#f5f1e8">PyObject*</span> <span style="color:#93c5fd">pk7vm_vm_loop</span>(<span style="color:#f5f1e8">PK7VMContext*</span> ctx) {
    <span style="color:#60a5fa">while</span> (ctx->active) {
        <span style="color:#60a5fa">uint8_t</span> op = *ctx->ip++ ^ ctx->rolling_key;
        ctx->rolling_key = (ctx->rolling_key * <span style="color:#c084fc">0x5DEECE66DULL</span> + <span style="color:#c084fc">0xB</span>) &amp; <span style="color:#c084fc">0xFF</span>;
        <span style="color:#60a5fa">switch</span> (op) {
            <span style="color:#60a5fa">case</span> <span style="color:#a3e635">0x4F</span>: handle_LOAD_FAST(ctx); <span style="color:#60a5fa">break</span>;
            <span style="color:#60a5fa">case</span> <span style="color:#a3e635">0x88</span>: handle_STORE_FAST(ctx); <span style="color:#60a5fa">break</span>;
            <span style="color:#60a5fa">case</span> <span style="color:#a3e635">0x1D</span>: handle_LOAD_CONST_ENC(ctx); <span style="color:#60a5fa">break</span>;
            <span style="color:#60a5fa">case</span> <span style="color:#a3e635">0xB9</span>: handle_CALL_NATIVE(ctx); <span style="color:#60a5fa">break</span>;
            <span style="color:#60a5fa">default</span>:   pk7vm_anti_tamper_kill(); <span style="color:#60a5fa">return</span> <span style="color:#f87171">NULL</span>;
        }
    }
}`;

const STAGE_IDA_ASSEMBLY = `<span style="color:#6a7282">; Sealed Native Extension: app.pyd (PE32+ / x86_64)</span>
<span style="color:#6a7282">; Stripped binary view in IDA Pro / Hex-Rays / Ghidra</span>
<span style="color:#94a3b8">.text:00007FFB3A101040</span> <span style="color:#93c5fd">sub_7FFB3A101040</span> <span style="color:#60a5fa">proc near</span>
<span style="color:#94a3b8">.text:00007FFB3A101040</span>  <span style="color:#60a5fa">mov</span>     [rsp+<span style="color:#c084fc">10h</span>], rbx
<span style="color:#94a3b8">.text:00007FFB3A101045</span>  <span style="color:#60a5fa">mov</span>     [rsp+<span style="color:#c084fc">18h</span>], rsi
<span style="color:#94a3b8">.text:00007FFB3A10104A</span>  <span style="color:#60a5fa">push</span>    rdi
<span style="color:#94a3b8">.text:00007FFB3A10104B</span>  <span style="color:#60a5fa">sub</span>     rsp, <span style="color:#c084fc">30h</span>
<span style="color:#94a3b8">.text:00007FFB3A10104F</span>  <span style="color:#60a5fa">call</span>    <span style="color:#f87171">sub_7FFB3A104E90</span>           <span style="color:#6a7282">; Anti-debug / PEB integrity trap</span>
<span style="color:#94a3b8">.text:00007FFB3A101054</span>  <span style="color:#60a5fa">test</span>    eax, eax
<span style="color:#94a3b8">.text:00007FFB3A101056</span>  <span style="color:#60a5fa">jnz</span>     <span style="color:#60a5fa">short</span> loc_7FFB3A10108A     <span style="color:#6a7282">; Silent state divergence on tamper</span>
<span style="color:#94a3b8">.text:00007FFB3A10105C</span>  <span style="color:#60a5fa">mov</span>     rsi, [rsp+<span style="color:#c084fc">48h</span>]             <span style="color:#6a7282">; Encrypted bytecode buffer</span>
<span style="color:#94a3b8">.text:00007FFB3A101061</span>  <span style="color:#60a5fa">movzx</span>   eax, <span style="color:#60a5fa">byte ptr</span> [rsi]        <span style="color:#6a7282">; Fetch polymorphic opcode</span>
<span style="color:#94a3b8">.text:00007FFB3A101064</span>  <span style="color:#60a5fa">xor</span>     eax, <span style="color:#c084fc">0x88B1</span>                <span style="color:#6a7282">; Mutate with rolling build key</span>
<span style="color:#94a3b8">.text:00007FFB3A101069</span>  <span style="color:#60a5fa">jmp</span>     <span style="color:#60a5fa">qword ptr</span> [r12+rax*8]      <span style="color:#6a7282">; Indirect jump into switch table</span>`;

const PIPELINE_STAGES = [
  {
    id: 0,
    label: 'Source',
    desc: 'Plain Python, exposed logic',
    accent: '#f87171',
    code: STAGE_SOURCE,
    lang: 'source.py',
    operation: 'Syntax Parsing & Scope Tree Isolation',
    breakdown: 'Parses raw Python 3.11–3.13 AST, isolates designated protected function scopes, strips docstrings, and separates proprietary control flow from public runtime interfaces.',
    impact: 'Raw Python source exposes all literals, variables, and algorithms directly to 1-click decompilers (pycdc, uncompyle6).',
  },
  {
    id: 1,
    label: 'AST Mutation',
    desc: 'Names & structure randomized',
    accent: '#fb923c',
    code: STAGE_AST,
    lang: 'ast_mutated.py',
    operation: 'Symbol Anonymization & Opaque Predicates',
    breakdown: 'Rewrites variable identifiers, inserts dead-code branches, flattens linear control flow, and injects mathematical opaque invariants that appear non-deterministic.',
    impact: 'Automated symbol recovery fails. Control flow graph analyzers cannot reliably determine function boundaries or call trees.',
  },
  {
    id: 2,
    label: 'Bytecode',
    desc: 'Custom stack machine opcodes',
    accent: '#60a5fa',
    code: STAGE_BYTECODE,
    lang: 'vm_bytecode.asm',
    operation: 'Proprietary Instruction Synthesis',
    breakdown: 'Eliminates CPython bytecode objects (PyCodeObject) completely. Compiles AST into an opaque stack machine instruction set with randomized opcode numbering per build.',
    impact: 'Disassemblers looking for standard CPython bytecode (LOAD_FAST, CALL_FUNCTION) find no code objects to parse. .pyc-oriented tools have no bytecode to work on.',
  },
  {
    id: 3,
    label: 'C++ Generation',
    desc: 'Opaque C++ dispatch loop',
    accent: '#c084fc',
    code: STAGE_CPP,
    lang: 'vm_dispatch.cpp',
    operation: 'Polymorphic Engine Generation',
    breakdown: 'Synthesizes a standalone C++ virtual machine dispatch loop. Handlers, opcode decode keys, and register indexing are derived from a per-build 256-bit cryptographic seed.',
    impact: 'No two builds share dispatch table layouts or opcode numbers. Analysis produced against one build (opcode maps, handler offsets, seeds) does not apply to the next.',
  },
  {
    id: 4,
    label: 'Native Binary',
    desc: 'Sealed x86_64 native assembly',
    accent: '#10b981',
    code: STAGE_IDA_ASSEMBLY,
    lang: 'app.pyd (IDA Pro Disassembly)',
    operation: 'MSVC Optimization & HMAC Anti-Debug Sealing',
    breakdown: 'Compiles C++ sources into a native Windows (.pyd) or Linux (.so) extension for x64 or ARM64. Inlines debugger detection, out-of-process memory-read monitoring, and integrity checks.',
    impact: 'Python-level analysis tools find no code objects to inspect. Native disassemblers (IDA/Ghidra) see an indirect dispatch loop and encrypted constant payloads.',
  },
];

const DEFENSE_CARDS = [
  {
    title: 'No baseline to match',
    body: 'Every build is a new binary. Opcodes, keys, and symbols are re-randomized per compilation. A signature or opcode table recovered from one build does not describe the next.',
    icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  },
  {
    title: 'Static tools return nothing',
    body: 'Disassemblers open a valid executable. The interesting parts are not there. CPython code objects and string tables are completely eradicated.',
    icon: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',
  },
  {
    title: 'Emulators run blind',
    body: 'The binary knows whether it\'s running in a real interpreter. In a synthetic or hooked environment, checks silently produce wrong answers.',
    icon: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  },
  {
    title: 'Strings don\'t exist in plaintext',
    body: 'There are no plaintext strings to scan. No heap spray. No regex that finds a secret by pattern. What you\'re looking for was never stored unencrypted.',
    icon: 'M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18',
  },
  {
    title: 'Dumps capture a moving target',
    body: 'The runtime scans for foreign processes holding memory-read handles on the module and scrubs decrypted state on detection. Constants are decrypted transiently, not held as a plaintext pool.',
    icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  },
  {
    title: 'No stable read window',
    body: 'Decrypted constants materialize on demand and are wiped after use. A single memory snapshot exposes only what was live at that instant, not the full constant pool.',
    icon: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22v-7',
  },
];

// ─── HERO SECTION ───────────────────────────────────────────────────────────
function TechHero() {
  const [btn1Hover, setBtn1Hover] = useState(false);
  const [btn2Hover, setBtn2Hover] = useState(false);

  return (
    <section id="hero" style={{ position: 'relative', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 'clamp(96px, 12vw, 140px) clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px)' }}>
      <div style={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 'clamp(320px, 90vw, 900px)', height: '600px', background: 'radial-gradient(ellipse at center, rgba(59,130,246,0.12) 0%, rgba(30,58,138,0.04) 50%, transparent 75%)', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: 880, width: '100%', margin: '0 auto' }}>
        <Reveal delay={40}>
          <h1 style={{ fontSize: 'clamp(34px, 5.5vw, 76px)', fontWeight: 500, letterSpacing: -2, lineHeight: 1.08, margin: '0 auto 20px', color: 'var(--text)', textWrap: 'balance' }}>
            Five stages.{' '}
            <span style={{ fontWeight: 300, backgroundImage: 'linear-gradient(180deg, var(--text) 0%, var(--brand) 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>
              Zero bytecode.
            </span>
          </h1>
        </Reveal>

        <Reveal delay={80}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(15px, 2vw, 16.5px)', lineHeight: 1.65, maxWidth: 660, margin: '0 auto 16px', fontFamily: 'var(--font-ui)' }}>
            PyK7VM strips Python AST and bytecode entirely, compiling proprietary logic into an opaque polymorphic native virtual machine. Disassemblers see only encrypted payloads and a private stack machine.
          </p>
        </Reveal>

        <Reveal delay={120}>
          <p style={{ color: 'var(--text-muted)', fontSize: 'clamp(13px, 1.8vw, 14px)', lineHeight: 1.6, maxWidth: 720, margin: '0 auto 32px', fontFamily: 'var(--font-ui)' }}>
            PyInstaller bundles bytecode that extracts in seconds. PyArmor decrypts into CPython's eval loop. Cython uses hookable C-API calls. PyK7VM replaces execution with an isolated native memory space with per-build opcodes.
          </p>
        </Reveal>

        <Reveal delay={160}>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="register.php"
              onMouseEnter={() => setBtn1Hover(true)}
              onMouseLeave={() => setBtn1Hover(false)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--text)', color: 'var(--bg)',
                padding: '13px 26px', borderRadius: 999, fontWeight: 600, fontSize: 14.5,
                textDecoration: 'none', letterSpacing: -0.2,
                boxShadow: '0 4px 20px rgba(245,241,232,0.18)',
                transform: btn1Hover ? 'translateY(-2px)' : 'none',
                transition: 'transform .25s ease, box-shadow .25s ease', minHeight: 46,
              }}
            >
              Get started free
            </a>
            <a
              href="docs.php"
              onMouseEnter={() => setBtn2Hover(true)}
              onMouseLeave={() => setBtn2Hover(false)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(245,241,232,0.03)', color: 'var(--text-dim)',
                border: `1px solid ${btn2Hover ? 'rgba(59,130,246,0.45)' : 'var(--border-strong)'}`,
                padding: '13px 24px', borderRadius: 999, fontWeight: 500, fontSize: 14.5,
                textDecoration: 'none', transition: 'border-color .25s ease, color .25s ease', minHeight: 46,
              }}
            >
              Read documentation
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── PIPELINE SECTION WITH DEEP EXPLANATIONS ────────────────────────────────
function PipelineSection() {
  const [active, setActive] = useState(0);
  const stage = PIPELINE_STAGES[active];

  return (
    <section id="pipeline" style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal delay={80}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 50px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 14, maxWidth: 720, color: 'var(--text)' }}>
            From source to{' '}
            <span style={{ fontWeight: 300, color: 'var(--brand-soft)' }}>virtualized binary.</span>
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, maxWidth: 620, marginBottom: 36 }}>
            Click any stage to inspect what the code looks like at that point in the pipeline. Every build produces different artifacts at every stage.
          </p>
        </Reveal>

        <style>{`
          .pipe-layout { display: flex; gap: 24px; align-items: flex-start; }
          .pipe-stage-nav { display: flex; flex-direction: column; gap: 8px; flex-shrink: 0; min-width: 220px; }
          @media (max-width: 920px) {
            .pipe-layout { flex-direction: column !important; }
            .pipe-stage-nav {
              flex-direction: row !important;
              overflow-x: auto !important;
              width: 100% !important;
              padding-bottom: 8px !important;
              -webkit-overflow-scrolling: touch;
            }
            .pipe-stage-nav button { min-width: 170px !important; }
          }
          @media (min-width: 921px) {
            .pipe-layout { flex-direction: row !important; }
            .pipe-stage-nav {
              flex-direction: column !important;
              overflow-x: visible !important;
              width: auto !important;
              padding-bottom: 0 !important;
            }
            .pipe-stage-nav button { min-width: 0 !important; width: 100% !important; }
          }
          @keyframes pipe-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        `}</style>

        <Reveal delay={180}>
          <div className="pipe-layout">
            
            {/* Left/Top: Stage Navigation Buttons */}
            <div className="pipe-stage-nav">
              {PIPELINE_STAGES.map((s, i) => {
                const isActive = active === i;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActive(i)}
                    className={`pipe-stage-btn ${isActive ? 'is-active' : ''}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 16px', borderRadius: 12,
                      background: isActive ? 'rgba(59,130,246,0.12)' : 'var(--surface)',
                      border: isActive ? '1px solid rgba(59,130,246,0.45)' : '1px solid var(--border)',
                      cursor: 'pointer', textAlign: 'left', transition: 'all .25s ease',
                      boxShadow: isActive ? '0 4px 20px rgba(59,130,246,0.12)' : 'none',
                      minHeight: 44,
                    }}
                  >
                    <span
                      style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: isActive ? s.accent : 'var(--text-muted)',
                        flexShrink: 0, transition: 'background .25s',
                        boxShadow: isActive ? `0 0 8px ${s.accent}` : 'none',
                      }}
                    />
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: isActive ? 'var(--text)' : 'var(--text-dim)', letterSpacing: -0.2, transition: 'color .25s', whiteSpace: 'nowrap' }}>
                        {s.label}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: -0.1, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.desc}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Right: Code Display & Deep Engineering Breakdown Card */}
            <div style={{ flex: 1, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: 18 }}>
              
              {/* Terminal Code Window */}
              <div
                className="pipeline-terminal stay-dark"
                style={{
                  background: '#090912', border: `1px solid ${stage.accent}44`,
                  borderRadius: 16, fontFamily: 'var(--font-code)', fontSize: 'clamp(11px, 2vw, 12.5px)',
                  lineHeight: 1.75, overflow: 'hidden', boxShadow: `0 0 35px ${stage.accent}12`,
                  transition: 'border-color .35s, box-shadow .35s', width: '100%',
                }}
              >
                {/* Terminal Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981' }} />
                  </div>
                  <span style={{ marginLeft: 8, fontSize: 11, color: '#a1a1aa', letterSpacing: 0.3 }}>{stage.lang}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, letterSpacing: 1, color: stage.accent, textTransform: 'uppercase', background: `${stage.accent}15`, padding: '2px 8px', borderRadius: 999 }}>
                    stage {active + 1} of 5
                  </span>
                </div>

                {/* Code Body */}
                <div key={active} style={{ padding: 'clamp(14px, 3vw, 20px)', animation: 'pipe-fade .3s ease both', overflowX: 'auto', WebkitOverflowScrolling: 'touch', background: 'transparent' }}>
                  <pre className="stay-dark" style={{ margin: 0, whiteSpace: 'pre', background: 'transparent', color: '#f1f5f9' }} dangerouslySetInnerHTML={{ __html: stage.code }} />
                </div>
              </div>

              {/* In-Depth Technical Breakdown Card */}
              <div
                className="card panel pipe-deep-card"
                style={{
                  background: 'var(--surface)', border: '1px solid var(--border-strong)',
                  borderRadius: 16, padding: 'clamp(18px, 3vw, 24px)', backdropFilter: 'blur(10px)',
                  width: '100%', minWidth: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: stage.accent, fontFamily: 'var(--font-code)' }}>
                    Stage {active + 1} Technical Spec
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-code)' }}>
                    {stage.operation}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 'clamp(14px, 3vw, 24px)' }} className="pipe-deep-grid">
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                      In-Depth Compiler Transformation
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.6, margin: 0 }}>
                      {stage.breakdown}
                    </p>
                  </div>

                  <div className="defense-impact-box" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: stage.accent, marginBottom: 4 }}>
                      Defense Impact
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
                      {stage.impact}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Reveal>
      </div>
      <style>{`
        @media (max-width: 860px) {
          .pipe-deep-grid { grid-template-columns: 1fr !important; gap: 14px !important; }
        }
        html.light .pipe-stage-btn:not(.is-active) {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
        }
        html.light .pipe-stage-btn.is-active {
          background: #eff6ff !important;
          border-color: rgba(37, 99, 235, 0.4) !important;
        }
        html.light .pipe-deep-card {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
        }
        html.light .defense-impact-box {
          background: #f8fafc !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
        }
      `}</style>
    </section>
  );
}

// ─── UNIFIED POLYMORPHIC VM ARCHITECTURE SECTION ───────────────────────────
function PolymorphicArchitectureSection() {
  const [buildSeed, setBuildSeed] = useState(0x88B1);
  const [recompiling, setRecompiling] = useState(false);
  const [sampleOpcodes, setSampleOpcodes] = useState({
    loadFast: '0x4F',
    storeFast: '0x88',
    loadConst: '0x1D',
    callNative: '0xB9',
  });

  const randHex = () => '0x' + Math.floor(Math.random() * 256).toString(16).toUpperCase().padStart(2, '0');

  const simulateNewBuild = async () => {
    if (recompiling) return;
    setRecompiling(true);
    for (let t = 0; t < 8; t++) {
      await _delay(70);
      setBuildSeed(Math.floor(0x1000 + Math.random() * 0xEFFF));
      setSampleOpcodes({
        loadFast: randHex(),
        storeFast: randHex(),
        loadConst: randHex(),
        callNative: randHex(),
      });
    }
    const finalSeed = Math.floor(0x1000 + Math.random() * 0xEFFF);
    setBuildSeed(finalSeed);
    setSampleOpcodes({
      loadFast: randHex(),
      storeFast: randHex(),
      loadConst: randHex(),
      callNative: randHex(),
    });
    setRecompiling(false);
  };

  return (
    <section id="vm-architecture" style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal delay={80}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 50px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 14, maxWidth: 720, color: 'var(--text)' }}>
            A VM that's{' '}
            <span style={{ fontWeight: 300, color: 'var(--brand-soft)' }}>never been seen before.</span>
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, maxWidth: 640, marginBottom: 36 }}>
            Every build generates a randomized opcode layout, encrypted registers, and rolling keys. Two compilations of identical Python source share zero static binary signatures.
          </p>
        </Reveal>

        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.95fr', gap: 'clamp(24px, 4vw, 36px)', alignItems: 'center' }} className="vm-arch-split">
          
          {/* Left Column: Technical Narrative */}
          <div>
            <Reveal delay={180}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="card poly-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6, letterSpacing: -0.2 }}>
                    Per-Build Randomized Opcode Map
                  </h3>
                  <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
                    Opcode values mutate with every compilation. Decompilers cannot use static lookup tables to map handlers to Python bytecode instructions.
                  </p>
                </div>

                <div className="card poly-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6, letterSpacing: -0.2 }}>
                    Rolling Key Decode Loop
                  </h3>
                  <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
                    Each opcode is XOR-decoded with a rolling LCG pseudo-random key that advances after every executed instruction. Static byte scanning yields meaningless noise.
                  </p>
                </div>

                <div className="card poly-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6, letterSpacing: -0.2 }}>
                    Transient Constant Decryption
                  </h3>
                  <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
                    Strings and constants stay encrypted in the binary. They are decrypted in RAM only for the duration of active execution, then immediately zero-wiped.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Live Interactive Opcode Mutator Card */}
          <div style={{ minWidth: 0 }}>
            <Reveal delay={240}>
              <div
                className="card poly-gen-card"
                style={{
                  background: 'var(--surface)', border: '1px solid var(--border-strong)',
                  borderRadius: 18, padding: 'clamp(18px, 4vw, 28px)', backdropFilter: 'blur(16px)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 30px rgba(59,130,246,0.08)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--brand-soft)', fontFamily: 'var(--font-code)' }}>
                    Live Polymorphic Generator
                  </span>
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-code)', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 999 }}>
                    Seed: 0x{buildSeed.toString(16).toUpperCase()}
                  </span>
                </div>

                {/* Sample Opcode Map */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontFamily: 'var(--font-code)', fontSize: 12, marginBottom: 20 }}>
                  <div className="poly-opcode-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--text-dim)' }}>LOAD_FAST (local scope)</span>
                    <span style={{ color: '#3b82f6', fontWeight: 700 }}>{sampleOpcodes.loadFast}</span>
                  </div>
                  <div className="poly-opcode-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--text-dim)' }}>STORE_FAST (register write)</span>
                    <span style={{ color: '#a855f7', fontWeight: 700 }}>{sampleOpcodes.storeFast}</span>
                  </div>
                  <div className="poly-opcode-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--text-dim)' }}>LOAD_CONST_ENC (lazy decrypt)</span>
                    <span style={{ color: '#f59e0b', fontWeight: 700 }}>{sampleOpcodes.loadConst}</span>
                  </div>
                  <div className="poly-opcode-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--text-dim)' }}>CALL_NATIVE (C hot-path)</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>{sampleOpcodes.callNative}</span>
                  </div>
                </div>

                {/* Action Button */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 10 }}>
                  <button
                    onClick={simulateNewBuild}
                    disabled={recompiling}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.4)',
                      color: 'var(--brand-soft)', padding: '10px 18px', borderRadius: 999,
                      fontWeight: 600, fontSize: 13, cursor: recompiling ? 'default' : 'pointer',
                      transition: 'all .25s ease', opacity: recompiling ? 0.6 : 1, minHeight: 40,
                    }}
                  >
                    <span>{recompiling ? 'Synthesizing build…' : 'Simulate new compilation'}</span>
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-code)' }}>
                    64 unique opcodes
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .vm-arch-split { grid-template-columns: 1fr !important; gap: 28px !important; }
        }
      `}</style>
    </section>
  );
}

// ─── DEFENSE SECTION ────────────────────────────────────────────────────────
function DefenseSection() {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="defense" style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal delay={80}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 50px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 12, maxWidth: 700, color: 'var(--text)' }}>
            Analysis{' '}
            <span style={{ fontWeight: 300, color: 'var(--brand-soft)' }}>starts cold.</span>
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, maxWidth: 560, marginBottom: 40 }}>
            Standard tooling hits a surface it wasn't built for. The cheap shortcuts and decompiler scripts are gone.
          </p>
        </Reveal>

        <style>{`
          @media (max-width: 720px) { .defense-grid { grid-template-columns: 1fr !important; } }
          @media (max-width: 1024px) and (min-width: 721px) { .defense-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        `}</style>

        <div className="defense-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {DEFENSE_CARDS.map((card, i) => (
            <Reveal key={card.title} delay={i * 50}>
              <div
                className="card defense-card"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  background: 'var(--surface)',
                  border: `1px solid ${hovered === i ? 'rgba(59,130,246,0.45)' : 'var(--border-strong)'}`,
                  borderRadius: 18, padding: 'clamp(20px, 3.5vw, 28px)',
                  transition: 'border-color .3s, transform .3s ease',
                  transform: hovered === i ? 'translateY(-3px)' : 'none',
                  backdropFilter: 'blur(10px)', height: '100%',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: hovered === i ? 'rgba(59,130,246,0.15)' : 'rgba(245,241,232,0.04)', border: `1px solid ${hovered === i ? 'rgba(59,130,246,0.4)' : 'rgba(245,241,232,0.08)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={hovered === i ? 'var(--brand-soft)' : 'var(--text-muted)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'stroke .3s' }}>
                      {card.icon.split(' M ').map((p, j) => <path key={j} d={(j === 0 ? '' : 'M ') + p} />)}
                    </svg>
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.3, marginBottom: 8, color: 'var(--text)' }}>
                    {card.title}
                  </h3>
                  <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
                    {card.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CONTROL FLOW FLATTENING (CFG) SECTION ───────────────────────────────────
function ControlFlowFlatteningSection() {
  return (
    <section id="cfg" style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal delay={60}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 14, color: 'var(--text)' }}>
            Control flow flattening{' '}
            <span style={{ fontWeight: 300, color: 'var(--brand-soft)' }}>erases the call graph.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16px)', lineHeight: 1.65, maxWidth: 720, margin: '0 0 32px' }}>
            Standard Python compilers emit linear sequences of bytecode with predictable jump offsets. PyK7VM breaks control structures into isolated basic blocks governed by a state-driven central dispatcher loop with opaque predicate variables.
          </p>
        </Reveal>

        <Reveal delay={160}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, alignItems: 'stretch' }}>
            {/* Standard CFG Card */}
            <div className="card cfg-card" style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 18, padding: 'clamp(18px, 3vw, 24px)', fontFamily: 'var(--font-ui)', minWidth: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 4 }}>
                <span style={{ fontSize: 14.5, fontWeight: 600, color: '#f87171' }}>Standard Python Bytecode</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-code)' }}>CPython 3.11+</span>
              </div>
              <div className="terminal-box stay-dark" style={{ background: '#090912', borderRadius: 10, padding: '14px', border: '1px solid rgba(255,255,255,0.08)', fontFamily: 'var(--font-code)', fontSize: 11.5, lineHeight: 1.6, color: '#f1f5f9', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ color: '#a1a1aa' }}># Predictable Hierarchical Flow</div>
                <div>def verify_license(key):</div>
                <div style={{ paddingLeft: 14, color: '#d4d4d8' }}>if not key: return False</div>
                <div style={{ paddingLeft: 14, color: '#f87171' }}>&gt; POP_JUMP_FORWARD_IF_FALSE</div>
                <div style={{ paddingLeft: 14, color: '#d4d4d8' }}>sig = hash_sha256(key)</div>
                <div style={{ paddingLeft: 14, color: '#d4d4d8' }}>return sig == EXPECTED_SIG</div>
              </div>
              <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Decompilers reconstruct AST syntax trees directly in &lt;100ms.
              </div>
            </div>

            {/* Flattened CFG Card */}
            <div className="card cfg-card" style={{
              background: 'var(--surface)',
              border: '1px solid rgba(59,130,246,0.4)', borderRadius: 18,
              padding: 'clamp(18px, 3vw, 24px)', fontFamily: 'var(--font-ui)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)', minWidth: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 4 }}>
                <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--brand-soft)' }}>PyK7VM Flattened Dispatcher</span>
                <span style={{ fontSize: 11, color: 'var(--success)', fontFamily: 'var(--font-code)' }}>Opaque Loop</span>
              </div>
              <div className="terminal-box stay-dark" style={{ background: '#090912', borderRadius: 10, padding: '14px', border: '1px solid rgba(59,130,246,0.3)', fontFamily: 'var(--font-code)', fontSize: 11.5, lineHeight: 1.6, color: '#f1f5f9', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ color: '#93c5fd' }}>// Switch-Case State Graph</div>
                <div>uint32_t state = 0x8F3C9102;</div>
                <div>while (state != 0x0) &#123;</div>
                <div style={{ paddingLeft: 14 }}>switch (state ^ key_entropy) &#123;</div>
                <div style={{ paddingLeft: 24, color: '#60a5fa' }}>case 0x1A: exec_block_4(); break;</div>
                <div style={{ paddingLeft: 24, color: '#60a5fa' }}>case 0x4F: exec_decoy(); break;</div>
                <div style={{ paddingLeft: 24, color: '#60a5fa' }}>case 0x9B: exec_block_1(); break;</div>
                <div style={{ paddingLeft: 14 }}>&#125;</div>
                <div>&#125;</div>
              </div>
              <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--text-dim)', lineHeight: 1.5 }}>
                Control flow graph is collapsed into 1 flat dispatcher with no visible call hierarchy.
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── COMPETITOR COMPARISON GRID ──────────────────────────────────────────────
function CompetitorComparisonSection() {
  const compRows = [
    {
      metric: 'Bytecode & AST Stripped',
      desc: 'Standard CPython code objects removed entirely from final artifact',
      pk7vm: 'Yes (100% Native)',
      pyinstaller: 'No (Bundled archive)',
      pyarmor: 'No (Decrypted in RAM)',
      cython: 'Partial (C-API bindings)',
    },
    {
      metric: 'Execution Engine',
      desc: 'Underlying virtual machine or runtime executing the code',
      pk7vm: 'Polymorphic Stack VM',
      pyinstaller: 'CPython 3.x runtime',
      pyarmor: 'CPython Frame Eval Loop',
      cython: 'CPython C-API calls',
    },
    {
      metric: 'Per-Build Polymorphism',
      desc: 'Randomized opcodes, instruction sets, and entropy per compilation',
      pk7vm: 'Yes (64 Opcodes)',
      pyinstaller: 'No (Deterministic)',
      pyarmor: 'No (Static loader)',
      cython: 'No (Static C code)',
    },
    {
      metric: 'Memory Dump & Frame-Hook Resistance',
      desc: 'Exposure of protected logic to runtime hooks and live memory dumping',
      pk7vm: 'No CPython eval frame; dump detection + key scrub',
      pyinstaller: 'Vulnerable',
      pyarmor: 'Vulnerable (Hooks / Dumps)',
      cython: 'Vulnerable to C-API hooks',
    },
    {
      metric: 'Anti-Debug & Self-Destruct',
      desc: 'Active PEB traps, hardware breakpoint detection, zero-fill scrub',
      pk7vm: 'Yes (Multi-Layered)',
      pyinstaller: 'No',
      pyarmor: 'Basic check',
      cython: 'No',
    },
    {
      metric: 'Decompiler Resistance',
      desc: 'Resistance against automated decompilers (pycdc, uncompyle6, Ghidra)',
      pk7vm: 'High (no CPython code objects)',
      pyinstaller: 'Zero (1-click extraction)',
      pyarmor: 'Low to Medium',
      cython: 'Medium (C decompilation)',
    },
  ];

  return (
    <section id="comparison-grid" style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal delay={60}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 14, color: 'var(--text)' }}>
            How PyK7VM compares to{' '}
            <span style={{ fontWeight: 300, color: 'var(--brand-soft)' }}>traditional tools.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16px)', lineHeight: 1.6, maxWidth: 720, margin: '0 0 36px' }}>
            Packagers bundle Python; obfuscators encrypt scripts but decrypt them in memory; compilers translate syntax to CPython API calls. PyK7VM replaces the CPython interpreter for protected functions.
          </p>
        </Reveal>

        <Reveal delay={160}>
          <div className="table-responsive-wrapper table-sticky-col">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Security Capability</th>
                  <th style={{ width: '26%', background: 'rgba(59,130,246,0.12)', color: 'var(--brand-soft)' }}>PyK7VM</th>
                  <th style={{ width: '15%' }}>PyInstaller</th>
                  <th style={{ width: '15%' }}>PyArmor</th>
                  <th style={{ width: '16%' }}>Cython</th>
                </tr>
              </thead>
              <tbody>
                {compRows.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13.5 }}>{r.metric}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{r.desc}</div>
                    </td>
                    <td style={{ background: 'rgba(59,130,246,0.04)', color: 'var(--text)', fontWeight: 600 }}>
                      <span style={{ color: '#10b981', marginRight: 6 }}>✓</span>
                      {r.pk7vm}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{r.pyinstaller}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{r.pyarmor}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{r.cython}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── SECURITY BOUNDARIES SECTION ─────────────────────────────────────────────
function SecurityBoundariesSection() {
  return (
    <section id="boundaries" style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal delay={60}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 14, color: 'var(--text)' }}>
            Security boundaries{' '}
            <span style={{ fontWeight: 300, color: 'var(--brand-soft)' }}>defined honestly.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16px)', lineHeight: 1.6, maxWidth: 720, margin: '0 0 36px' }}>
            No client-side protection system is mathematically unbreakable on hardware owned by an adversary. PyK7VM forces reverse engineering costs higher than the commercial value of the underlying intellectual property.
          </p>
        </Reveal>

        <Reveal delay={160}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {/* In-Scope Protection */}
            <div style={{
              background: 'linear-gradient(180deg, rgba(16,185,129,0.04) 0%, rgba(245,241,232,0.01) 100%)',
              border: '1px solid rgba(16,185,129,0.3)', borderRadius: 18,
              padding: 'clamp(20px, 3vw, 28px)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                  In-Scope Protection
                </h3>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.75 }}>
                <li>✓ Proprietary core algorithms, weights &amp; logic</li>
                <li>✓ Hardcoded API credentials, tokens &amp; seeds</li>
                <li>✓ License validation &amp; node-locking verification</li>
                <li>✓ Elimination of automated 1-click decompilers</li>
                <li>✓ Detection &amp; response for debugger attachment and memory dumping</li>
              </ul>
            </div>

            {/* Out-of-Scope Risks */}
            <div style={{
              background: 'linear-gradient(180deg, rgba(239,68,68,0.04) 0%, rgba(245,241,232,0.01) 100%)',
              border: '1px solid rgba(239,68,68,0.25)', borderRadius: 18,
              padding: 'clamp(20px, 3vw, 28px)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                  Out-of-Scope Risks (Physical Limits)
                </h3>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.75 }}>
                <li>✕ Ring-0 kernel rootkits with supervisor memory tapping</li>
                <li>✕ Intentionally exposed public function return values</li>
                <li>✕ Host OS compromises with physical bus debuggers</li>
                <li>✕ Black-box I/O profiling of public endpoints</li>
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── PERFORMANCE & HOT-PATHS SECTION ────────────────────────────────────────
function PerformanceOptimizationSection() {
  return (
    <section id="performance" style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal delay={60}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 14, color: 'var(--text)' }}>
            Balancing security{' '}
            <span style={{ fontWeight: 300, color: 'var(--brand-soft)' }}>and raw speed.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16px)', lineHeight: 1.6, maxWidth: 720, margin: '0 0 36px' }}>
            Virtualizing every trivial loop in a CPU-bound compute routine creates unnecessary overhead. PyK7VM gives developers granular decorators to protect proprietary logic with the VM while running compute loops at native speed.
          </p>
        </Reveal>

        {/* 3 Metric Cards */}
        <Reveal delay={160}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }}>
            <div style={{ background: 'rgba(245,241,232,0.015)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 16px' }}>
              <div style={{ fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700, color: 'var(--brand-soft)', letterSpacing: -1 }}>1.1x – 1.8x</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>Typical Business Logic</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Execution overhead on API handlers and licensing checks.</div>
            </div>
            <div style={{ background: 'rgba(245,241,232,0.015)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 16px' }}>
              <div style={{ fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700, color: '#10b981', letterSpacing: -1 }}>5 – 20 ms</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>Module Cold Start</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>One-time PE loading, entropy seeds, and opcode setup.</div>
            </div>
            <div style={{ background: 'rgba(245,241,232,0.015)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 16px' }}>
              <div style={{ fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700, color: '#60a5fa', letterSpacing: -1 }}>1.0x (Native)</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>Auto-Detected Hot Paths</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Compute loops run at raw compiled C-speed automatically.</div>
            </div>
          </div>
        </Reveal>

        {/* Code Example Card */}
        <Reveal delay={200}>
          <div className="terminal-window stay-dark" style={{ background: '#09090f', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, padding: 'clamp(16px, 3vw, 24px)', fontFamily: 'var(--font-code)', fontSize: 'clamp(11px, 2vw, 12.5px)', lineHeight: 1.65, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: 8 }}># Auto-detection: sensitive IP virtualized, compute loops run natively</div>
            <div><span style={{ color: '#60a5fa' }}>def</span> <span style={{ color: '#f1f5f9' }}>validate_tenant_license</span>(tenant_token: str) -&gt; bool:</div>
            <div style={{ paddingLeft: 18, color: 'var(--text-dim)' }}>return cryptographic_license_eval(tenant_token)  <span style={{ color: '#6a7282' }}># auto-virtualized</span></div>
            <br />
            <div><span style={{ color: '#60a5fa' }}>def</span> <span style={{ color: '#f1f5f9' }}>matrix_multiply_hotpath</span>(a: list, b: list) -&gt; list:</div>
            <div style={{ paddingLeft: 18, color: 'var(--text-dim)' }}>return [sum(i * j for i, j in zip(r, c)) for c in zip(*b) for r in a]  <span style={{ color: '#6a7282' }}># auto-native</span></div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── CLOSING SECTION ────────────────────────────────────────────────────────
function ClosingSection() {
  const [btnHover, setBtnHover] = useState(false);

  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 24px)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 'clamp(300px, 80vw, 800px)', height: '500px', background: 'radial-gradient(ellipse at center, rgba(59,130,246,0.12) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

      <Reveal>
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(26px, 4.5vw, 50px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 16, color: 'var(--text)' }}>
            Protection that ships at build time.
          </h2>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, maxWidth: 460, margin: '0 auto 32px' }}>
            Your proprietary logic is compiled into a polymorphic native VM with a per-build instruction set — no CPython bytecode for decompilers to recover.
          </p>
          <a
            href="register.php"
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--text)', color: 'var(--bg)',
              padding: '13px 30px', borderRadius: 999, fontWeight: 600, fontSize: 14.5,
              textDecoration: 'none', letterSpacing: -0.2,
              boxShadow: '0 4px 20px rgba(245,241,232,0.18)',
              transform: btnHover ? 'translateY(-2px)' : 'none',
              transition: 'transform .25s ease, box-shadow .25s ease', minHeight: 46,
            }}
          >
            Get started free
          </a>
        </div>
      </Reveal>
    </section>
  );
}

// ─── MAIN TECHNOLOGY APP ROOT ───────────────────────────────────────────────
function TechnologyApp() {
  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  return (
    <div style={{ position: 'relative', overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
      <style>{`
        html.light .tech-card,
        html.light .poly-card,
        html.light .poly-gen-card,
        html.light .defense-card,
        html.light .cfg-card,
        html.light .opcode-table-box,
        html.light .cff-preview-box,
        html.light .boundary-card,
        html.light .perf-matrix-box,
        html.light .closing-card {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
          color: #0f172a !important;
        }
        html.light .poly-opcode-row {
          background: #f8fafc !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
        }
      `}</style>
      <Nav />
      <TechHero />
      <PipelineSection />
      <PolymorphicArchitectureSection />
      <ControlFlowFlatteningSection />
      <CompetitorComparisonSection />
      <DefenseSection />
      <SecurityBoundariesSection />
      <PerformanceOptimizationSection />
      <ClosingSection />
      <Footer />
      <DiscordCTA />
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(TechnologyApp));
